/**
 * Created by Ning Jiang on 2021-06-07.
 */


import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {ReelState} from "../../../core/reel/ReelState";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Oktoberfest} from "../../Oktoberfest";
import {OktoberfestGameConfig} from "../../OktoberfestGameConfig";
import {OktoberfestSymbolState} from "../../symbol/OktoberfestSymbolConfig";
import {OktoberfestSymbol} from "../../symbol/OktoberfestSymbol";

export class WildNudgeController {

    private readonly _nudgeStepDuration: number = 1.4+1;//drop+land animation timing
    private readonly _nudgeEase: string = `elastic.out(1.1, 1)`;
    private _steps: number[];

    constructor() {
    }

    public initAnimations(): void {
        if (this.hasWin()) {
            this.createWinAnimation();
        }
    }

    protected hasWin(): boolean {
        return Oktoberfest.model.parsedGameData.overlayWilds[2].indexOf("W") > 0;
    }

    // Please NOTE: if no win, the data can complete before the actual animation is rendered. So eg, if you update the xNudge wild multiplier on step start/complete,
    // you might get the wp complete before the actual animation because the framerate is bigger than the animation duration.

    // So you have to make sure everything is really complete in onReelNudgeComplete() so you don't get a confused state in the next wp.
    protected async createWinAnimation(): Promise<void> {
        this._steps = ArrayHelper.initArrayWithValues<number>(OktoberfestGameConfig.REELS_NUM, () => 0);
        const hasWin = Oktoberfest.model.parsedGameData.betWins?.length > 0;
        let delay = 0;
        const promises: Promise<void>[] = [];
        const reelId: number = 2;
        if (Oktoberfest.model.parsedGameData.mode === "WILD_RESPIN") {
            const promise: Promise<void> = new Promise<void>(resolve => {
                    this._steps[2] = 1;
                    Oktoberfest.reels[reelId].nudge(
                        this._steps[reelId],
                        hasWin ? this._nudgeStepDuration : 0.01,
                        this._nudgeEase,
                        delay,
                        (stepsLeft: number, aborted: boolean) => this.onNudgeStepStart(reelId, stepsLeft, aborted),
                        (stepsLeft: number, aborted: boolean) => this.onNudgeStepComplete(reelId, stepsLeft, aborted),
                        async (aborted: boolean) => {
                            await this.onReelNudgeComplete(reelId, aborted);
                            resolve();
                        },
                        false
                    );
                    delay += hasWin ? this.getNextReelNudgeDelayDelta(reelId) : 0.01;
                }
            );
            promises.push(promise);
        }
        await Promise.all(promises);
    }

    public skip() {
        Oktoberfest.reels[2].abortNudge();
    }

    // The range is the startIndex (include) in the reel symbols to the endIndex (NOT include).
    // For a 3x5 slot, there are 3 symbols in reel, then the range is [0, 3],

    protected getNextReelNudgeDelayDelta(reelId: number): number {
        return Math.abs(this._steps[reelId]) * (this._nudgeStepDuration);
    }

    private onNudgeStepStart(reelId: number, stepsLeft: number, aborted: boolean): void {
        const currentWildindex: number = Oktoberfest.model.parsedGameData.overlayWilds[2].indexOf("W");
        const nudgeSymbol: OktoberfestSymbol = Oktoberfest.reels[2].getSymbol(currentWildindex);
        if (nudgeSymbol.symbolName == "W3" && !aborted) {
            nudgeSymbol.getStateAnimation(
                OktoberfestSymbolState.DROP,
                {duration: 0.01, alpha: 1},
                {duration: 0.01, alpha: 0}
            );
        }
        this.showNonWinningSymbolsOnReel2();
        this.showNormalSymbolsOnReel2(currentWildindex);
        nudgeSymbol.visible = true;
        Logger.logDev(`Reel ${reelId} start nudge step, stepsLeft = ${stepsLeft}, abort = ${aborted}`);
    }

    private onNudgeStepComplete(reelId: number, stepsLeft: number, aborted: boolean): void {
        Logger.logDev(`Reel ${reelId} completed nudge step, stepsLeft = ${stepsLeft}, abort = ${aborted}`);
    }

    private onReelNudgeComplete(reelId: number, aborted: boolean): void {
        let currentWildindex: number = Oktoberfest.model.parsedGameData.overlayWilds[2].indexOf("W");
        let reelW3Sym: OktoberfestSymbol = Oktoberfest.reels[2].getSymbol(currentWildindex);
        reelW3Sym.visible = true;
        if (!aborted) {
            reelW3Sym.getStateAnimation(
                OktoberfestSymbolState.LAND,
                {duration: 0.01, alpha: 1},
                {duration: 0.01, alpha: 0}
            );
        }
        Logger.logDev(`Reel ${reelId} completed reel nudge, abort = ${aborted}`);
    }

    private showNormalSymbolsOnReel2(currentWildindex: number): void {
        if (currentWildindex !== 0) {
            Oktoberfest.reels[2].getSymbol(0).getStateAnimation(
                OktoberfestSymbolState.NORMAL,
                {duration: 0.01, alpha: 1},
                {duration: 0.01, alpha: 0}
            );
        }
        if (currentWildindex !== 1) {
            Oktoberfest.reels[2].getSymbol(1).getStateAnimation(
                OktoberfestSymbolState.NORMAL,
                {duration: 0.01, alpha: 1},
                {duration: 0.01, alpha: 0}
            );
        }
    }

    private showNonWinningSymbolsOnReel2(): void {
        Oktoberfest.reels[2].getSymbol(0, true).getStateAnimation(
            OktoberfestSymbolState.NO_WIN,
            {duration: 0.01, alpha: 1},
            {duration: 0.01, alpha: 0}
        );
        Oktoberfest.reels[2].getSymbol(1, true).getStateAnimation(
            OktoberfestSymbolState.NO_WIN,
            {duration: 0.01, alpha: 1},
            {duration: 0.01, alpha: 0}
        );
    }
}